import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    AlertTriangle,
    CheckCircle2,
    Facebook,
    Globe,
    Loader2,
    Plug,
    RefreshCw,
    Unplug,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/hooks/useConfirm';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { useCreateCampaign, useUpdateCampaign } from '@/api/campaigns/use-campaigns';
import {
    useCampaignFacebookPageForms,
    useCampaignFacebookStatus,
    useConnectCampaignFacebook,
    useDisconnectCampaignFacebook,
    useLinkCampaignFacebookForm,
} from '@/api/campaigns/use-campaign-facebook';
import type { Campaign, CampaignSource } from '@/api/campaigns/types';
import FacebookPermissionList from '@/components/campaigns/FacebookPermissionList';
import {
    parseFacebookConnectError,
    parseFacebookExpiry,
    type FacebookConnectError,
} from '@/components/campaigns/facebookPermissions';

export interface SourceStepProps {
    mode: 'create' | 'edit';
    campaign: Campaign | null;
    /** Create mode only: called with the new campaign's id once it (and its Facebook link, if any) is saved. */
    onCreated: (campaignId: string) => void;
    /** Edit mode only: called once the step's changes are saved, to advance to step 2. */
    onSavedAndAdvance: () => void;
}

const tokenTypeLabel = (type: string): string =>
    type === 'SYSTEM_USER' ? 'System User' : type === 'USER' ? 'User' : type;

/**
 * Editor step 1 (field-mapping.md §7.1): name, description, source choice.
 * Facebook campaigns validate the System User token saved in Integrations
 * ("Connect"), pick a page, and link one of its (not-yet-linked) lead forms here.
 */
const SourceStep: React.FC<SourceStepProps> = ({
    mode,
    campaign,
    onCreated,
    onSavedAndAdvance,
}) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();

    const [name, setName] = useState(campaign?.name ?? '');
    const [description, setDescription] = useState(campaign?.description ?? '');
    const [source, setSource] = useState<CampaignSource>(campaign?.source ?? 'landing_page');
    const [selectedPageId, setSelectedPageId] = useState(campaign?.facebook_page_id ?? '');
    const [selectedFormId, setSelectedFormId] = useState(campaign?.facebook_form_id ?? '');
    const [importBacklog, setImportBacklog] = useState(false);
    const [connectError, setConnectError] = useState<FacebookConnectError | null>(null);

    const { data: fbStatusData } = useCampaignFacebookStatus();
    const fbStatus = fbStatusData?.data;
    const connectFacebook = useConnectCampaignFacebook();
    const disconnectFacebook = useDisconnectCampaignFacebook();
    const { data: formsData, isLoading: isLoadingForms } =
        useCampaignFacebookPageForms(selectedPageId);
    const forms = formsData?.data ?? [];

    const createCampaign = useCreateCampaign();
    const updateCampaign = useUpdateCampaign();
    const linkFacebookForm = useLinkCampaignFacebookForm();

    const isSaving =
        createCampaign.isPending || updateCampaign.isPending || linkFacebookForm.isPending;

    /** Connect (token present, not connected yet) and Sync pages (already connected) share this call. */
    const handleConnectFacebook = async (isSync: boolean) => {
        setConnectError(null);
        try {
            const result = await connectFacebook.mutateAsync();
            enqueueSnackbar(
                isSync
                    ? t('campaigns.editor.source.pagesSynced', { count: result.pages.length })
                    : t('campaigns.editor.source.facebookConnected'),
                { variant: 'success' }
            );
        } catch (error) {
            // 422: `{ errors: string[], missing_permissions?: string[] }` (no token saved,
            // invalid token / wrong app, or missing required permissions).
            const parsed = parseFacebookConnectError(error);
            if (parsed.errors.length === 0 && parsed.missingPermissions.length === 0) {
                parsed.errors = [t('campaigns.editor.source.facebookConnectFailed')];
            }
            setConnectError(parsed);
        }
    };

    const handleDisconnectFacebook = async () => {
        const ok = await confirm({
            title: t('campaigns.editor.source.disconnectConfirmTitle'),
            description: t('campaigns.editor.source.disconnectConfirmDescription'),
            confirmLabel: t('campaigns.editor.source.disconnectButton'),
            variant: 'destructive',
        });
        if (!ok) return;

        try {
            await disconnectFacebook.mutateAsync();
            setConnectError(null);
            setSelectedPageId('');
            setSelectedFormId('');
            enqueueSnackbar(t('campaigns.editor.source.disconnected'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('campaigns.editor.source.disconnectFailed'), { variant: 'error' });
        }
    };

    const handleSelectPage = (pageId: string) => {
        setSelectedPageId(pageId);
        setSelectedFormId('');
    };

    const canContinue =
        name.trim().length > 0 &&
        (source !== 'facebook' || (selectedPageId.length > 0 && selectedFormId.length > 0));

    const handleContinue = async () => {
        if (!canContinue) return;

        try {
            if (mode === 'create') {
                const created = await createCampaign.mutateAsync({
                    name: name.trim(),
                    description: description.trim() || null,
                    source,
                });
                if (source === 'facebook') {
                    await linkFacebookForm.mutateAsync({
                        id: created.id,
                        data: {
                            facebook_page_id: selectedPageId,
                            facebook_form_id: selectedFormId,
                            import_backlog: importBacklog,
                        },
                    });
                }
                enqueueSnackbar(t('campaigns.editor.source.created'), { variant: 'success' });
                onCreated(created.id);
            } else if (campaign) {
                await updateCampaign.mutateAsync({
                    id: campaign.id,
                    data: { name: name.trim(), description: description.trim() || null },
                });
                if (
                    source === 'facebook' &&
                    (selectedPageId !== campaign.facebook_page_id ||
                        selectedFormId !== campaign.facebook_form_id)
                ) {
                    await linkFacebookForm.mutateAsync({
                        id: campaign.id,
                        data: {
                            facebook_page_id: selectedPageId,
                            facebook_form_id: selectedFormId,
                            import_backlog: importBacklog,
                        },
                    });
                }
                enqueueSnackbar(t('campaigns.editor.source.saved'), { variant: 'success' });
                onSavedAndAdvance();
            }
        } catch {
            enqueueSnackbar(t('campaigns.editor.source.saveFailed'), { variant: 'error' });
        }
    };

    const sourceLocked = mode === 'edit';
    const expiresAt = parseFacebookExpiry(fbStatus?.connection?.token_expires_at);
    const missingRecommended = fbStatus?.connection?.missing_recommended ?? [];

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>{t('campaigns.editor.source.detailsTitle')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="campaign-name">{t('campaigns.editor.source.name')}</Label>
                        <Input
                            id="campaign-name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder={t('campaigns.editor.source.namePlaceholder')}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="campaign-description">
                            {t('campaigns.editor.source.description')}
                        </Label>
                        <Textarea
                            id="campaign-description"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder={t('campaigns.editor.source.descriptionPlaceholder')}
                            rows={3}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>{t('campaigns.editor.source.sourceLabel')}</Label>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <button
                                type="button"
                                disabled={sourceLocked}
                                onClick={() => setSource('facebook')}
                                className={cn(
                                    'flex items-center gap-3 rounded-lg border p-4 text-left transition-colors',
                                    source === 'facebook'
                                        ? 'border-primary bg-primary/5'
                                        : 'hover:border-muted-foreground/50',
                                    sourceLocked && 'cursor-not-allowed opacity-70'
                                )}
                            >
                                <Facebook className="h-5 w-5 text-blue-600" />
                                <div>
                                    <p className="font-medium text-sm">
                                        {t('campaigns.source.facebook')}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {t('campaigns.editor.source.facebookHint')}
                                    </p>
                                </div>
                            </button>
                            <button
                                type="button"
                                disabled={sourceLocked}
                                onClick={() => setSource('landing_page')}
                                className={cn(
                                    'flex items-center gap-3 rounded-lg border p-4 text-left transition-colors',
                                    source === 'landing_page'
                                        ? 'border-primary bg-primary/5'
                                        : 'hover:border-muted-foreground/50',
                                    sourceLocked && 'cursor-not-allowed opacity-70'
                                )}
                            >
                                <Globe className="h-5 w-5 text-emerald-600" />
                                <div>
                                    <p className="font-medium text-sm">
                                        {t('campaigns.source.landing_page')}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {t('campaigns.editor.source.landingPageHint')}
                                    </p>
                                </div>
                            </button>
                        </div>
                        {sourceLocked && (
                            <p className="text-xs text-muted-foreground">
                                {t('campaigns.editor.source.sourceLocked')}
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>

            {source === 'facebook' && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Facebook className="h-5 w-5" />
                            {t('campaigns.editor.source.facebookTitle')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {fbStatus && !fbStatus.configured && (
                            <Alert variant="warning">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription className="flex flex-wrap items-center gap-x-1">
                                    <span>{t('campaigns.editor.source.notConfigured')}</span>
                                    <Link to="/integrations" className="font-medium underline">
                                        {t('campaigns.editor.source.notConfiguredAction')}
                                    </Link>
                                </AlertDescription>
                            </Alert>
                        )}

                        {fbStatus?.configured && !fbStatus.has_token && (
                            <Alert variant="warning">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription className="flex flex-wrap items-center gap-x-1">
                                    <span>{t('campaigns.editor.source.noToken')}</span>
                                    <Link to="/integrations" className="font-medium underline">
                                        {t('campaigns.editor.source.notConfiguredAction')}
                                    </Link>
                                </AlertDescription>
                            </Alert>
                        )}

                        {fbStatus?.connection?.status === 'needs_reauth' && (
                            <Alert variant="warning">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>
                                    <p className="font-medium">
                                        {t('campaigns.editor.source.needsReauth')}
                                    </p>
                                    {fbStatus.connection.last_error && (
                                        <p>{fbStatus.connection.last_error}</p>
                                    )}
                                </AlertDescription>
                            </Alert>
                        )}

                        {fbStatus?.connected ? (
                            <Alert variant="success">
                                <CheckCircle2 className="h-4 w-4" />
                                <AlertDescription className="space-y-3">
                                    <div>
                                        <p className="font-medium">
                                            {t('campaigns.editor.source.connectedAs', {
                                                name: fbStatus.connection?.fb_user_name ?? '',
                                            })}
                                        </p>
                                        <p className="text-xs">
                                            {fbStatus.connection?.token_type && (
                                                <span>
                                                    {t('campaigns.editor.source.tokenType', {
                                                        type: tokenTypeLabel(
                                                            fbStatus.connection.token_type
                                                        ),
                                                    })}
                                                    {' · '}
                                                </span>
                                            )}
                                            {expiresAt ? (
                                                <span>
                                                    {t('campaigns.editor.source.expiresOn')}{' '}
                                                    <DateTimeFormatter date={expiresAt} />
                                                </span>
                                            ) : (
                                                <span>
                                                    {t('campaigns.editor.source.neverExpires')}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleConnectFacebook(true)}
                                            disabled={connectFacebook.isPending}
                                        >
                                            {connectFacebook.isPending ? (
                                                <Loader2 className="h-4 w-4 animate-spin me-2" />
                                            ) : (
                                                <RefreshCw className="h-4 w-4 me-2" />
                                            )}
                                            {t('campaigns.editor.source.syncPages')}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleDisconnectFacebook}
                                            disabled={disconnectFacebook.isPending}
                                        >
                                            {disconnectFacebook.isPending ? (
                                                <Loader2 className="h-4 w-4 animate-spin me-2" />
                                            ) : (
                                                <Unplug className="h-4 w-4 me-2" />
                                            )}
                                            {t('campaigns.editor.source.disconnectButton')}
                                        </Button>
                                    </div>
                                </AlertDescription>
                            </Alert>
                        ) : (
                            fbStatus?.configured &&
                            fbStatus.has_token && (
                                <div className="flex items-center justify-between gap-3 rounded-lg border p-4">
                                    <p className="text-sm text-muted-foreground">
                                        {t('campaigns.editor.source.notConnected')}
                                    </p>
                                    <Button
                                        onClick={() => handleConnectFacebook(false)}
                                        disabled={connectFacebook.isPending}
                                    >
                                        {connectFacebook.isPending ? (
                                            <Loader2 className="h-4 w-4 animate-spin me-2" />
                                        ) : (
                                            <Plug className="h-4 w-4 me-2" />
                                        )}
                                        {t('campaigns.editor.source.connectButton')}
                                    </Button>
                                </div>
                            )
                        )}

                        {connectError && (
                            <Alert variant="destructive">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription className="space-y-2">
                                    {connectError.errors.length > 0 && (
                                        <ul className="space-y-1">
                                            {connectError.errors.map(message => (
                                                <li key={message}>{message}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {connectError.missingPermissions.length > 0 && (
                                        <div className="space-y-1">
                                            <p className="font-medium">
                                                {t('campaigns.editor.source.missingPermissions')}
                                            </p>
                                            <FacebookPermissionList
                                                permissions={connectError.missingPermissions}
                                            />
                                            <p className="text-xs">
                                                {t(
                                                    'campaigns.editor.source.missingPermissionsHint'
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </AlertDescription>
                            </Alert>
                        )}

                        {fbStatus?.connected && missingRecommended.length > 0 && (
                            <Alert variant="warning">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription className="space-y-1">
                                    <p>{t('campaigns.editor.source.missingRecommended')}</p>
                                    <FacebookPermissionList permissions={missingRecommended} />
                                </AlertDescription>
                            </Alert>
                        )}

                        {fbStatus?.connected && (
                            <>
                                <div className="space-y-2">
                                    <Label>{t('campaigns.editor.source.pageLabel')}</Label>
                                    <Select value={selectedPageId} onValueChange={handleSelectPage}>
                                        <SelectTrigger>
                                            <SelectValue
                                                placeholder={t(
                                                    'campaigns.editor.source.pagePlaceholder'
                                                )}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {fbStatus.pages.map(page => (
                                                <SelectItem key={page.id} value={page.page_id}>
                                                    {page.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {selectedPageId && (
                                    <div className="space-y-2">
                                        <Label>{t('campaigns.editor.source.formLabel')}</Label>
                                        {isLoadingForms ? (
                                            <p className="text-sm text-muted-foreground">
                                                {t('campaigns.editor.source.loadingForms')}
                                            </p>
                                        ) : forms.length === 0 ? (
                                            <p className="text-sm text-muted-foreground">
                                                {t('campaigns.editor.source.noForms')}
                                            </p>
                                        ) : (
                                            <div className="space-y-2">
                                                {forms.map(form => {
                                                    const isLinkedElsewhere =
                                                        !!form.linked_campaign_id &&
                                                        form.linked_campaign_id !== campaign?.id;
                                                    const isSelected = selectedFormId === form.id;
                                                    return (
                                                        <button
                                                            key={form.id}
                                                            type="button"
                                                            disabled={isLinkedElsewhere}
                                                            onClick={() =>
                                                                setSelectedFormId(form.id)
                                                            }
                                                            className={cn(
                                                                'w-full rounded-md border p-3 text-left transition-colors',
                                                                isSelected &&
                                                                    'border-primary bg-primary/5',
                                                                isLinkedElsewhere &&
                                                                    'cursor-not-allowed opacity-50'
                                                            )}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-medium">
                                                                    {form.name}
                                                                </span>
                                                                <Badge variant="outline">
                                                                    {form.status}
                                                                </Badge>
                                                            </div>
                                                            {isLinkedElsewhere && (
                                                                <p className="mt-1 text-xs text-muted-foreground">
                                                                    {t(
                                                                        'campaigns.editor.source.formAlreadyLinked'
                                                                    )}
                                                                </p>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="import-backlog"
                                        checked={importBacklog}
                                        onCheckedChange={checked =>
                                            setImportBacklog(checked as boolean)
                                        }
                                    />
                                    <Label htmlFor="import-backlog" className="font-normal">
                                        {t('campaigns.editor.source.importBacklog')}
                                    </Label>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            <div className="flex items-center justify-end border-t pt-4">
                <Button onClick={handleContinue} disabled={!canContinue || isSaving}>
                    {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    {mode === 'create'
                        ? t('campaigns.editor.actions.createAndContinue')
                        : t('campaigns.editor.actions.saveAndContinue')}
                </Button>
            </div>
        </div>
    );
};

export default SourceStep;
